<template>
  <div v-if="workListItems.length > 0">
    <table class="table">
      <tr>
        <th class="header-column checkbox-column">
          <v-checkbox :disabled="workListItems.length === 0" v-model="allSelected" />
        </th>
        <th class="header-column" v-for="column in itemsColumns" :key="column.key">
          {{ $t(column.columnTranslationKey) }}
          <div ref="orderSortIcon" class="header-column__icon" @click="sortByKey($event, column)"></div>
        </th>
      </tr>
      <tr v-for="(item, index) in sortedItems" :key="item.id" class="item">
        <td @click.stop="() => {}" class="checkbox-column">
          <v-checkbox class="td-checkbox" :value="selectedItems.includes(item)" @input="toggleSelected(item)" />
        </td>
        <td @click.prevent="selectItem(index)">{{ item.medKey }}</td>
        <td @click.prevent="selectItem(index)">
          <a @click.prevent class="link">
            {{ item.medicineName }}
          </a>
        </td>
        <td @click.prevent="selectItem(index)">{{ item.manufacturer }}</td>
        <td @click.prevent="selectItem(index)">
          <div class="status-icon" :class="getStatus(item).key">
            {{ $t(getStatus(item).nameTranslationKey) }}
          </div>
        </td>
        <td class="no-pointer-events">
          <div
            class="counter-box"
            :class="{
              'counter-box--disabled':
                item.statusId !== READY_FOR_PRODUCTION_STATUS &&
                item.statusId !== ON_HOLD_STATUS &&
                !isSelected(item.id),
            }"
          >
            <span
              class="counter-box__minus"
              :class="!disabledItems.includes(item.id) && 'yes-pointer-events'"
              @click.stop="decreaseCanister(item)"
            >
              -
            </span>
            <span class="counter-box__number yes-pointer-events">
              <v-input
                v-if="amountEditMode === item.id"
                :value="item.amountOfCanisters"
                @change="setCanisterAmount(item, $event.target.value)"
                :has-error="!$v.amountOfCanistersOnPill.between"
              />
              <span v-if="amountEditMode !== item.id" @click="amountEditMode = item.id">
                {{ item.amountOfCanisters }}
              </span>
            </span>
            <span
              class="counter-box__plus"
              :class="!disabledItems.includes(item.id) && 'yes-pointer-events'"
              @click.stop="increaseCanister(item)"
            >
              +
            </span>
            <span
              class="counter-box__trash"
              :class="!disabledItems.includes(item.id) && 'yes-pointer-events'"
              @click.stop="removeCanisterAmount(item)"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M16.5 19.1001H6.5C5.94772 19.1001 5.5 18.6524 5.5 18.1001V4.1001H17.5V18.1001C17.5 18.6524 17.0523 19.1001 16.5 19.1001Z"
                  stroke="#004593"
                  stroke-linecap="square"
                />
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M14.5 4.1001H8.5V3.1001C8.5 2.54781 8.94772 2.1001 9.5 2.1001H13.5C14.0523 2.1001 14.5 2.54781 14.5 3.1001V4.1001Z"
                  stroke="#004593"
                  stroke-linecap="square"
                />
                <path d="M3.5 4.1001H19.5" stroke="#004593" stroke-linecap="square" />
                <path d="M8.5 8.1001V16.1001" stroke="#004593" stroke-linecap="square" />
                <path d="M11.5 8.1001V16.1001" stroke="#004593" stroke-linecap="square" />
                <path d="M14.5 8.1001V16.1001" stroke="#004593" stroke-linecap="square" />
              </svg>
            </span>
          </div>
        </td>
      </tr>
    </table>

    <div class="basket-popup" @click="onBasketClicked">
      <img src="~@/assets/icons/icon-basket.svg" />
      <div id="basket-counter" class="basket-popup__counter">{{ canisterAmountForOrder }}</div>
    </div>

    <v-work-list-item-details-popup
      v-if="selectedItemIndex !== null"
      :list-id="workListId"
      :item-id="selectedItemId"
      :first="selectedItemIndex === 0"
      :last="selectedItemIndex === workListItems.length - 1"
      @next="selectItem(selectedItemIndex + 1, true)"
      @prev="selectItem(selectedItemIndex - 1, true)"
      @close="$router.push({ name: 'myPills' })"
    />

    <v-pill-remove-popup
      @close="cancelRemovePill()"
      @delete="removePill()"
      v-if="showRemovePillPopup"
      :pill="pillToRemoveName"
    />
  </div>
  <div v-else class="empty-information">
    <div class="empty-information__img">
      <img src="~@/assets/welcome-page-img2.svg" />
    </div>
    <h2 class="empty-information__title">
      {{ $t("page.order.list.empty.title") }}
    </h2>
    <p class="empty-information__desc">
      {{ $t("page.order.list.empty.description") }}
    </p>
    <v-button primary @click="downloadExcelTemplateFile">
      <img src="~@/assets/icons/icon-download.svg" class="icon-left" />
      Excel template
    </v-button>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { VButton, VCheckbox, VInput } from "shared";
import VWorkListItemDetailsPopup from "../work-list/v-work-list-item-details-popup";
import VPillRemovePopup from "../pills/v-pill-remove-popup";
import * as ArrayUtils from "../../utils/ArrayUtils";
import * as DownloadUtils from "../../utils/DownloadUtils";
import * as WorklistsDAO from "@/dao/worklists-dao";
import { between } from "vuelidate/lib/validators";

export default {
  name: "VWorkList",
  components: {
    VWorkListItemDetailsPopup,
    VPillRemovePopup,
    VButton,
    VCheckbox,
    VInput,
  },
  data() {
    return {
      selectedItemIndex: null,
      selectedItems: [],
      disabledItems: [],
      sortDirection: "asc",
      sortKey: null,
      sortType: "string",
      showRemovePillPopup: false,
      pillToRemoveName: null,
      pillToRemoveId: null,
      pillToRemove: null,
      amountEditMode: null,
      amountOfCanistersOnPill: 0,
      READY_FOR_PRODUCTION_STATUS: 0,
      ON_HOLD_STATUS: 6,
    };
  },
  props: {
    statuses: {
      type: Array,
      default: () => [],
    },
    clearOptions: {
      type: Boolean,
      default: false,
    },
    showCheckDataPopup: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapGetters({
      canisterAmountForOrder: "workLists/canisterAmountForOrder",
      workListItems: "workLists/currentWorkList",
      workListId: "workLists/currentWorkListId",
      user: "user/userDetails",
      currentMedKeyTypeName: "medKey/currentMedKeyTypeName",
      pillAmountForCheckData: "workLists/pillAmountForCheckData",
    }),
    itemsColumns() {
      return [
        {
          key: "medKeys",
          type: "string",
          columnTranslationKey: this.currentMedKeyTypeName,
        },
        {
          key: "medicineName",
          type: "string",
          columnTranslationKey: "page.order.list.header.medicinename",
        },
        {
          key: "manufacturer",
          type: "string",
          columnTranslationKey: "page.order.list.header.manufacturer",
        },
        {
          key: "statusId",
          type: "string",
          columnTranslationKey: "page.order.list.header.statusid",
        },
        {
          key: "amountOfCanisters",
          type: "number",
          columnTranslationKey: "page.order.list.header.amountofcanisters",
        },
      ];
    },
    selectedItemId() {
      return this.workListItems[this.selectedItemIndex].id;
    },
    allSelected: {
      get() {
        return this.workListItems.length > 0 && this.selectedItems.length === this.workListItems.length;
      },

      set(allSelected) {
        this.selectedItems = allSelected ? [...this.workListItems] : [];
      },
    },
    sortedItems() {
      const items = this.workListItems;
      const stringCompare = (a, b) =>
        this.sortDirection === "asc"
          ? a[this.sortKey].toString().localeCompare(b[this.sortKey].toString())
          : b[this.sortKey].toString().localeCompare(a[this.sortKey].toString());
      const numberCompare = (a, b) =>
        this.sortDirection === "asc" ? a[this.sortKey] - b[this.sortKey] : b[this.sortKey] - a[this.sortKey];
      const compare = this.sortType === "string" ? stringCompare : numberCompare;
      return this.sortKey ? items.sort(compare) : items;
    },
  },

  methods: {
    ...mapActions({
      increase: "workLists/increase",
      decrease: "workLists/decrease",
      setAmount: "workLists/setAmount",
      getMyWorkListsById: "workLists/getMyWorkListsById",
    }),
    toggleSelected(item) {
      ArrayUtils.toggleInclude(this.selectedItems, item);
    },
    getStatus(item) {
      return this.statuses.find((status) => status.id === item.statusId);
    },
    sortByKey($event, { key, type }) {
      this.sortKey = key;
      this.sortType = type;
      if ($event.target.classList.contains("header-column__icon--toggle")) {
        $event.target.classList.remove("header-column__icon--toggle");
        this.sortDirection = "desc";
      } else {
        this.$refs.orderSortIcon.forEach((el) => {
          if (el.classList.contains("header-column__icon--toggle")) {
            el.classList.remove("header-column__icon--toggle");
          }
        });
        $event.target.classList.add("header-column__icon--toggle");
        this.sortDirection = "asc";
      }
    },
    async decreaseCanister(item) {
      this.amountOfCanistersOnPill = this.amountOfCanistersOnPill - 1;
      this.disabledItems.push(item.id);
      this.pillToRemoveId = null;
      if (item.amountOfCanisters === 1) {
        this.showRemovePillPopup = true;
        this.pillToRemoveName = `"${item.medicineName}, ${item.manufacturer}"`;
        this.pillToRemoveId = item.id;
        this.pillToRemove = item;
      } else {
        await this.decrease({ listId: this.workListId, pillId: item.id });
      }
      this.disabledItems = this.disabledItems.filter((i) => i !== item.id);
    },
    async increaseCanister(item) {
      this.amountOfCanistersOnPill = (isNaN(this.amountOfCanistersOnPill) ? 0 : this.amountOfCanistersOnPill) + 1;
      this.disabledItems.push(item.id);
      await this.increase({ listId: this.workListId, pillId: item.id });
      this.disabledItems = this.disabledItems.filter((i) => i !== item.id);
    },
    async setCanisterAmount(pill, value) {
      this.amountOfCanistersOnPill = value;
      pill.amountOfCanisters = this.amountOfCanistersOnPill;

      if (value.match(/[^$,.\d]/) || value < 0 || value > 999) {
        return;
      }

      this.amountEditMode = null;
      this.disabledItems.push(pill.id);
      this.pillToRemoveId = null;
      this.pillToRemove = null;
      if (value <= 0) {
        this.showRemovePillPopup = true;
        // TODO: clean this mess
        this.pillToRemoveName = `"${pill.medicineName}, ${pill.manufacturer}"`;
        this.pillToRemoveId = pill.id;
        this.pillToRemove = pill;
      } else {
        await this.setAmount({ listId: this.workListId, pill, value: value < 0 ? 0 : value });
      }
      this.disabledItems = this.disabledItems.filter((i) => i !== pill.id);
    },
    async removeCanisterAmount(pill) {
      this.showRemovePillPopup = true;
      this.pillToRemoveName = `"${pill.medicineName}, ${pill.manufacturer}"`;
      this.pillToRemoveId = pill.id;
      this.pillToRemove = pill;
    },
    async removePill() {
      this.showRemovePillPopup = false;
      await this.setAmount({ listId: this.workListId, pill: this.pillToRemove, value: 0 });
      await this.getMyWorkListsById();
    },
    cancelRemovePill() {
      this.showRemovePillPopup = false;
    },
    isSelected(pillId) {
      return this.selectedItems.some((o) => o.id === pillId);
    },
    onBasketClicked() {
      if (this.pillAmountForCheckData > 0) {
        this.$emit("update:showCheckDataPopup", true);
      } else {
        this.$router.push({ name: "orderSubmit", params: { workListId: this.workListId } });
      }
    },
    selectItem(index) {
      this.$router.push({ params: { pillId: this.workListItems[index].id } });
    },
    async downloadExcelTemplateFile() {
      const { excelTemplateUrl } = await WorklistsDAO.getTemplate();
      DownloadUtils.downloadFile(excelTemplateUrl, "template.xls");
    },
  },
  watch: {
    "$route.params.pillId": {
      handler(pillId) {
        this.selectedItemIndex = pillId ? this.workListItems.findIndex((item) => item.id == pillId) : null;
      },
      immediate: true,
    },
    selectedItems(value) {
      this.$emit("items-selected", value);
    },
    clearOptions(value) {
      if (value) {
        this.selectedItems = [];
      }
    },
  },
  validations: {
    amountOfCanistersOnPill: {
      between: between(0, 999),
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/variables";

.table {
  th {
    &:nth-child(1) {
      width: 4%;
    }
    &:nth-child(2) {
      width: 7%;
    }
    &:nth-child(3) {
      width: 25%;
    }
    &:nth-child(4) {
      width: 15%;
    }
    &:nth-child(5) {
      width: 11%;
    }
    &:nth-child(6) {
      width: 7%;
      min-width: 150px;
    }
  }
  tr {
    &:hover {
      cursor: pointer;
    }
  }
}

.link {
  &:hover {
    text-decoration: underline;
  }
}
.empty-information {
  margin: 120px auto;
  width: 100%;
  max-width: 245px;
  text-align: center;
  &__image {
    margin: 0 0 0 0;
  }
  &__title {
    font-size: 24px;
    line-height: 26px;
    text-align: center;
    letter-spacing: 0.8px;
    color: #000000;
    margin: 35px 0 23px 0;
  }
  &__desc {
    font-size: 13px;
    line-height: 16px;
    text-align: center;
    letter-spacing: 0.464286px;
    color: #000000;
    margin: 23px 0 40px 0;
  }
  .button {
    padding: 12px 17px 12px 35px;
  }
}

.status-icon {
  background-position: left center;
  background-repeat: no-repeat;
  padding-left: 30px;
  @media all and (max-width: 1200px) {
    line-height: 18px;
    padding-top: 2px;
    padding-bottom: 2px;
  }

  &.deviations,
  &.notCompatible,
  &.currentlyNotAvailable,
  &.currentlyNotAvailableCFR,
  &.newPill {
    background-image: url("~@/assets/icons/icon-missing-dimensions.svg");
    background-position: 2px center;
    opacity: 0.5;
  }
  &.onHold {
    background-image: url("~@/assets/icons/icon-missing-dimensions.svg");
    background-position: 2px center;
    opacity: 1;
  }

  &.severalOptions {
    background-image: url("~@/assets/icons/icon-several.svg");
    opacity: 0.5;
  }
  &.noMatch {
    background-image: url("~@/assets/icons/icon-missing-dimensions.svg");
    background-position: 2px center;
    opacity: 0.5;
  }
  &.readyForProduction {
    background-image: url("~@/assets/icons/icon-in-database.svg");
  }
}
.counter-box {
  margin: 0 auto;
}
.item:hover {
  .counter-box--disabled {
    pointer-events: auto;
    .counter-box__plus {
      background-image: url("~@/assets/icons/icon-plus.svg") !important;
    }
    .counter-box__minus {
      background-image: url("~@/assets/icons/icon-minus.svg") !important;
    }

    .counter-box__number {
      color: $color-black !important;
    }
  }
}

.basket-popup {
  background: #004593;
  width: 68px;
  height: 68px;
  border-radius: 50%;
  position: fixed;
  right: 50px;
  bottom: 50px;
  text-align: center;
  line-height: 50px;
  cursor: pointer;
  @media all and (max-width: 1200px) {
    right: 20px;
  }
  img {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }
  &__counter {
    background: #d15421;
    font-size: 13px;
    color: #fdfdfd;
    width: 27px;
    height: 27px;
    position: absolute;
    right: 0;
    top: -8px;
    border-radius: 50%;
    text-align: center;
    line-height: 27px;
  }
}
</style>
